import classnames from 'classnames';
import {
  cloneElement,
  forwardRef,
  useId,
  useImperativeHandle,
  useRef,
  useState,
  type AnimationEventHandler,
  type FocusEventHandler,
  type InputHTMLAttributes,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
} from 'react';

import { FormFieldHelper } from '../FormFieldHelper/FormFieldHelper';
import {
  FormFieldMessage,
  type MessageType,
} from '../FormFieldMessage/FormFieldMessage';

import styles from './Input.module.scss';

export type { MessageType };

export interface InputProps
  extends PropsWithChildren,
    Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  className?: string;
  contentClassName?: string;
  'data-testid'?: string;
  id?: string;
  inputClassName?: string;
  inputElement?: ReactElement;
  /** @deprecated Please use native "disabled" property instead. */
  isDisabled?: boolean;
  isReadOnly?: boolean;
  label?: string;
  message?: string | ReactElement;
  messageType?: MessageType;
  onAnimationStart?: AnimationEventHandler<HTMLInputElement>;
  onAutoFillCancel?: AnimationEventHandler<HTMLInputElement>;
  onAutoFillStart?: AnimationEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  placeholder?: string;
  prefix?: ReactNode;
  required?: boolean;
  suffix?: ReactNode;
  tag?: keyof JSX.IntrinsicElements;
  /** @deprecated Use data-testid instead. */
  testId?: string;
  type?: string;
}

/** @deprecated Use one of the input components in `@/core/tamagoshiTailwind/components/Input` instead. */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      children,
      className,
      contentClassName,
      disabled,
      'data-testid': dataTestId,
      id,
      inputClassName,
      inputElement,
      isDisabled: deprecatedIsDisabled,
      isReadOnly = false,
      label,
      message,
      messageType = 'danger',
      onAnimationStart,
      onAutoFillCancel,
      onAutoFillStart,
      onBlur,
      onFocus,
      placeholder,
      prefix,
      required = false,
      suffix,
      tag: Tag = 'div',
      testId: oldTestId,
      type,
      value,
      ...restProps
    },
    ref,
  ) => {
    const testId = dataTestId ?? oldTestId;
    const fallbackId = useId();
    const inputRef = useRef<HTMLInputElement>(null);
    const [isFocused, setIsFocused] = useState(false);
    const [autofilled, setAutofilled] = useState(false);
    const haveLabel = !!label;
    const actualId = haveLabel ? id || fallbackId : id;
    const isDisabled = deprecatedIsDisabled ?? disabled ?? false;

    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    /**
     * According to the HTML specifications, an input type=number will return an
     * empty string when filled with non-numerical values. That makes the input
     * believe the input is not filled and displays the label over the text.
     */
    const isNumberInputWronglyFlaggedAsEmpty =
      !value && type === 'number' && inputRef.current?.validity.badInput;

    const isLabelOn =
      (haveLabel && isFocused) ||
      !!value ||
      autofilled ||
      isNumberInputWronglyFlaggedAsEmpty;
    const isError = messageType === 'danger' && !!message;
    const isSuccess = messageType === 'success' && !!message;
    const havePlaceHolder = !!placeholder;

    const containerClassName = classnames(className, styles.container);
    const inputComputedClassName = classnames(styles.input, inputClassName);
    const contentClassNames = classnames(styles.content, contentClassName, {
      [styles.focused]: isFocused,
      [styles.disabled]: isDisabled,
      [styles.noLabel]: !haveLabel,
      [styles.error]: isError,
      [styles.success]: isSuccess,
    });
    const labelClassName = classnames(styles.label, {
      [styles.on]: isLabelOn,
      [styles.required]: required,
    });

    const onFocusHandler: FocusEventHandler<HTMLInputElement> = (event) => {
      if (isDisabled) {
        event.stopPropagation();
        event.preventDefault();
        return;
      }

      setIsFocused(true);
      if (onFocus) {
        onFocus(event);
      }
    };

    const onBlurHandler: FocusEventHandler<HTMLInputElement> = (event) => {
      setIsFocused(false);
      if (onBlur) {
        onBlur(event);
      }
    };

    const onAnimationStartHandler: AnimationEventHandler<HTMLInputElement> = (
      event,
    ) => {
      if (event.animationName === 'onAutoFillStart') {
        setAutofilled(true);
        if (onAutoFillStart) {
          onAutoFillStart(event);
        }
      } else if (event.animationName === 'onAutoFillCancel') {
        setAutofilled(false);
        if (onAutoFillCancel) {
          onAutoFillCancel(event);
        }
      }

      if (onAnimationStart) {
        onAnimationStart(event);
      }
    };

    return (
      <Tag className={containerClassName}>
        <div className={contentClassNames}>
          {prefix}
          <div className={styles.wrapper}>
            {inputElement ? (
              cloneElement(inputElement, {
                onAnimationStart: onAnimationStartHandler,
                onBlur: onBlurHandler,
                onFocus: onFocusHandler,
              })
            ) : (
              <input
                aria-errormessage={
                  isError && typeof message === 'string' ? message : undefined
                }
                aria-invalid={isError}
                className={inputComputedClassName}
                data-testid={testId}
                disabled={isDisabled}
                id={actualId}
                onAnimationStart={onAnimationStartHandler}
                onBlur={onBlurHandler}
                onFocus={onFocusHandler}
                placeholder={
                  havePlaceHolder && (!haveLabel || isLabelOn)
                    ? placeholder
                    : undefined
                }
                readOnly={isReadOnly}
                ref={inputRef}
                required={required}
                type={type}
                value={value}
                {...restProps}
              />
            )}
            {label && (
              <label htmlFor={actualId} className={labelClassName}>
                {label}
              </label>
            )}
          </div>
          {suffix}
        </div>
        {message && (
          <FormFieldMessage
            data-testid={testId}
            message={message}
            messageType={messageType}
          />
        )}
        {children && (
          <FormFieldHelper data-testid={testId}>{children}</FormFieldHelper>
        )}
      </Tag>
    );
  },
);

Input.displayName = 'Input';
